import { useEffect, useRef } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface WechatShareOptions {
  title: string;
  desc: string;
  link: string;
  imgUrl: string;
}

/**
 * 动态加载微信 JSSDK 脚本
 */
function loadWechatScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).wx) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('微信 JSSDK 加载失败'));
    document.head.appendChild(script);
  });
}

/**
 * 获取 JSSDK 配置
 */
async function fetchJsConfig(url: string) {
  try {
    const res = await fetch(`${API_BASE_URL}/wechat/jsconfig?url=${encodeURIComponent(url)}`);
    const data = await res.json();
    if (data.success) return data.data;
    return null;
  } catch {
    return null;
  }
}

/**
 * 配置微信分享
 * @param options 分享内容
 */
export function useWechatShare(options?: WechatShareOptions | null) {
  const initedRef = useRef(false);

  useEffect(() => {
    // 只在微信内置浏览器中生效，且必须有有效配置
    const isWechat = /MicroMessenger/i.test(navigator.userAgent);
    if (!isWechat || !options?.title || initedRef.current) return;

    const init = async () => {
      try {
        await loadWechatScript();
        const wx = (window as any).wx;
        if (!wx) return;

        const config = await fetchJsConfig(window.location.href.split('#')[0]);
        if (!config) {
          console.warn('[WechatShare] JSSDK config not available');
          return;
        }

        wx.config({
          debug: false,
          appId: config.appId,
          timestamp: config.timestamp,
          nonceStr: config.nonceStr,
          signature: config.signature,
          jsApiList: ['updateAppMessageShareData', 'updateTimelineShareData'],
        });

        wx.ready(() => {
          initedRef.current = true;

          // 分享给朋友
          wx.updateAppMessageShareData({
            title: options.title,
            desc: options.desc,
            link: options.link,
            imgUrl: options.imgUrl,
            success: () => console.log('[WechatShare] Message share set'),
          });

          // 分享到朋友圈
          wx.updateTimelineShareData({
            title: options.title,
            link: options.link,
            imgUrl: options.imgUrl,
            success: () => console.log('[WechatShare] Timeline share set'),
          });
        });

        wx.error((err: any) => {
          console.error('[WechatShare] wx.config error:', err);
        });
      } catch (err) {
        console.error('[WechatShare] Init failed:', err);
      }
    };

    init();
  }, [options?.title, options?.desc, options?.link, options?.imgUrl]);
}

export default useWechatShare;
